import { useState, type FormEvent } from "react";
import { contactInfo } from "@/mocks/guagua";
import SectionHeading from "./SectionHeading";

const interests = ["明星八卦", "社会热点", "娱乐综艺", "科技奇闻", "体育赛场", "民生趣闻"];

function Contact() {
  const [interests, setInterests] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const toggleInterest = (name: string) =>
    setInterests((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = ((fd.get("website_alt") as string) || "").trim();

    setStatus("loading");
    setFormError("");

    if (honeypot) {
      setStatus("success");
      form.reset();
      setInterests([]);
      return;
    }

    fd.delete("website_alt");

    try {
      const res = await fetch(
        "https://readdy.ai/api/form/da20islv7gurs51ef2h0",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
        }
      );
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setInterests([]);
      } else {
        setStatus("error");
        setFormError(typeof msg === "string" ? msg : "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="联系与订阅"
          title="订阅热点，瓜不缺席"
          desc="留下常用邮箱，我们会在每日热榜更新与重大焦点事件发生时，第一时间把最新动态推送到你手中。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-xl text-foreground-900">
                {contactInfo.slogan}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                有任何建议、版权反馈或合作意向，欢迎随时通过以下方式与我们联系，我们会尽快回复。
              </p>
              <ul className="mt-6 flex flex-col gap-4">
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="grid h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-mail-fill text-lg" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">邮箱</span>
                    {contactInfo.email}
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="grid h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-wechat-fill text-lg" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">微信</span>
                    {contactInfo.wechat}
                  </span>
                </li>
              </ul>
            </div>

            <div className="flex items-center gap-3 rounded-2xl border border-secondary-200 bg-secondary-50 p-5">
              <span className="grid h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                <i className="ri-customer-service-2-fill text-xl" aria-hidden="true"></i>
              </span>
              <p className="text-sm text-foreground-700">
                遇到内容问题或有线索爆料？欢迎联系编辑部，我们珍视每一位瓜友的反馈。
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7">
            <h3 className="text-lg font-medium text-foreground-900">订阅热点通知</h3>
            <p className="mt-2 text-sm text-foreground-600">
              选择你关心的领域，我们会据此推送更有针对性的热点动态。
            </p>

            <form
              data-readdy-form="da20islv7gurs51ef2h0"
              className="mt-5 flex flex-col gap-5"
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="nickname" className="text-sm text-foreground-700">
                    你的昵称
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    placeholder="怎么称呼你"
                    className="rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm text-foreground-700">
                    常用邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm text-foreground-700">感兴趣的分类</span>
                <div className="flex flex-wrap gap-2">
                  {interests.map((item) => (
                    <label
                      key={item}
                      className={`cursor-pointer select-none rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                        interests.includes(item)
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        name="interest"
                        value={item}
                        checked={interests.includes(item)}
                        onChange={() => toggleInterest(item)}
                        className="sr-only"
                      />
                      {item}
                    </label>
                  ))}
                </div>
              </div>

              <div className="hp-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-6 py-3 text-sm whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                    正在提交…
                  </>
                ) : (
                  <>
                    <i className="ri-notification-3-line" aria-hidden="true"></i>
                    订阅热点推送
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>
                  订阅成功！我们会把最新热点动态推送到你的邮箱，感谢关注。
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-error-warning-fill" aria-hidden="true"></i>
                  {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;